import { useMemo } from 'react';

export type DataItem = {
  id: string;
  createdAt: string;
  updatedAt: string;
  'sender.text': string;
  'receiver.text': string;
  sortedItemNumber: string;
  _meta: any;
};

type Node = {
  id: number;
  name: string;
  connections: number[];
  amount: number;
};

/**
 * Maps connection data into an array of Nodes with connection references.
 * Takes an array of data items (or any items) and transforms them into
 * a graph structure where each node knows which other nodes it's connected to.
 *
 * @param data - Array of items containing sender and receiver connection information
 * @returns Array of Node objects with connection references
 *
 * @example
 * // Sample input:
 * const data = [
 *   { 'sender.text': 'Alice', 'receiver.text': 'Bob' },
 *   { 'sender.text': 'Bob', 'receiver.text': 'Charlie' },
 *   { 'sender.text': 'Alice', 'receiver.text': 'Charlie' }
 * ];
 *
 * // Sample output:
 * [
 *   {
 *     id: 1,
 *     name: 'Alice',
 *     connections: [2, 3]  // Connected to Bob (id:2) and Charlie (id:3)
 *     amount: 0
 *   },
 *   {
 *     id: 2,
 *     name: 'Bob',
 *     connections: [1, 3]  // Connected to Alice (id:1) and Charlie (id:3)
 *     amount: 0
 *   },
 *   {
 *     id: 3,
 *     name: 'Charlie',
 *     connections: [1, 2]  // Connected to Alice (id:1) and Bob (id:2)
 *     amount: 0
 *   }
 * ]
 */
export const mapConnections = (data: DataItem[] | any[]): Node[] => {
  const nameToId = new Map<string, number>();
  const nodes: Node[] = [];
  const receiverAmounts = new Map<string, number>(); // Only tracks amounts where name is receiver
  let uniqueId = 1;

  // First pass: collect amounts only when name is receiver (receiver.text)
  data.forEach((item) => {
    const receiverName = item['receiver.text'];
    const amount = parseFloat(item['amount.amount']) || 0;

    if (receiverAmounts.has(receiverName)) {
      receiverAmounts.set(
        receiverName,
        receiverAmounts.get(receiverName)! + amount
      );
    } else {
      receiverAmounts.set(receiverName, amount);
    }
  });

  // Second pass: create nodes and connections
  data.forEach((item) => {
    const senderName = item['sender.text'];
    const receiverName = item['receiver.text'];

    // Create node for sender (with amount 0 if not a receiver elsewhere)
    if (!nameToId.has(senderName)) {
      nameToId.set(senderName, uniqueId);
      nodes.push({
        id: uniqueId,
        name: senderName,
        connections: [],
        amount: receiverAmounts.get(senderName) || 0, // Only if they're also a receiver
      });
      uniqueId++;
    }

    // Create node for receiver (with their total received amount)
    if (!nameToId.has(receiverName)) {
      nameToId.set(receiverName, uniqueId);
      nodes.push({
        id: uniqueId,
        name: receiverName,
        connections: [],
        amount: receiverAmounts.get(receiverName)!,
      });
      uniqueId++;
    }

    // Add connections
    const senderId = nameToId.get(senderName)!;
    const receiverId = nameToId.get(receiverName)!;

    const senderNode = nodes.find((n) => n.id === senderId)!;
    const receiverNode = nodes.find((n) => n.id === receiverId)!;

    if (!senderNode.connections.includes(receiverId)) {
      senderNode.connections.push(receiverId);
    }

    if (!receiverNode.connections.includes(senderId)) {
      receiverNode.connections.push(senderId);
    }
  });

  return nodes.filter((data) => data.name);
};

export const allAmountsZero = (nodes: Node[]): boolean => {
  if (nodes.length === 0) return true;
  return nodes.every((node) => node.amount === 0);
};

export const useStaticNodes = () => {
  return useMemo(
    () => [
      { id: 1, name: 'Click', connections: [6], amount: 1 },
      { id: 2, name: 'MikoSea', connections: [1, 3, 4, 5, 6], amount: 1 },
      { id: 3, name: 'ジャック', connections: [1, 4], amount: 0 },
      { id: 4, name: 'トシ', connections: [1, 3], amount: 1 },
      { id: 5, name: 'タロウ', connections: [1], amount: 0 },
      { id: 6, name: 'ユウコ', connections: [1], amount: 2 },
    ],
    []
  );
};
